/**
 * 共通日時ユーティリティ（U16「DATETIME JST統一」）。
 *
 * spica の全レイヤで日時は「JST の壁時計値」として一貫して扱う（datetime-contract.md §1）。
 * API から受信する日時文字列は `yyyy-MM-ddTHH:mm:ss`（オフセットなし・Zなし・秒精度）であり、
 * フロントは **`new Date(isoString)` に通さず、文字列操作で整形して表示** する（frontend-display-spec.md §1/§3）。
 * これにより実行環境TZに一切依存しない。
 *
 * 送信も同フォーマットのローカル文字列で送る（`toISOString()` によるUTC化を廃止、Q4=A）。
 *
 * 禁止事項（datetime-contract.md §4）:
 * - `new Date(isoString)` による表示用パース（実行環境TZ依存）
 * - `.toISOString()` による送信（UTC化）
 * - 日時文字列への `Z` / `+09:00` 付与
 *
 * 曜日算出のみ `new Date(y, m-1, d)`（数値引数 → 壁時計解釈でTZ非依存）を本ユーティリティ内に隔離して使用する
 * （frontend-display-spec.md §2.3。文字列パース `new Date(iso)` のみを禁止）。
 */

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

/** JSTローカル日時文字列を年月日時分秒に分解した結果。 */
export interface DateTimeParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
  second: number; // 0-59
}

/**
 * JSTローカル日時文字列（`yyyy-MM-ddTHH:mm:ss` / `yyyy-MM-ddTHH:mm` / `yyyy-MM-dd`）を
 * 文字列操作で年月日時分秒に分解する（`new Date()` 不使用、TZ非依存）。
 *
 * オフセット・Z付きの文字列や不正フォーマットは **fail-closed** で `null` を返す（SECURITY-15）。
 */
export function parseJstDateTime(iso: string | null | undefined): DateTimeParts | null {
  if (!iso) return null;
  // オフセット/Z付きは契約違反（UTC混入）のため受け付けない（fail-closed）。
  if (/[zZ]$/.test(iso) || /[+-]\d{2}:\d{2}$/.test(iso)) return null;

  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = m[4] !== undefined ? Number(m[4]) : 0;
  const minute = m[5] !== undefined ? Number(m[5]) : 0;
  const second = m[6] !== undefined ? Number(m[6]) : 0;

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;

  return { year, month, day, hour, minute, second };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * 年月日から曜日インデックス（0=日〜6=土）を算出する。
 * `new Date(y, m-1, d)` は数値引数のため壁時計解釈となりTZ非依存（frontend-display-spec.md §2.3）。
 */
export function weekdayIndex(year: number, month: number, day: number): number {
  return new Date(year, month - 1, day).getDay();
}

/** 曜日ラベル（"日"〜"土"）を返す。 */
export function weekdayLabel(year: number, month: number, day: number): string {
  return WEEKDAY_LABELS[weekdayIndex(year, month, day)];
}

/**
 * 年月日時分を JST ローカル送信文字列 `yyyy-MM-ddTHH:mm:ss` に組み立てる（`toISOString()` 不使用）。
 * 送信箇所（アポイントリクエストの希望日時等）で使用する。
 */
export function buildRequestDateTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0
): string {
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;
}

/** 日付キー（`YYYY-MM-DD`）と時刻（`HH:MM`）から JST 送信文字列を組み立てる。 */
export function buildRequestDateTimeFromKey(dateKey: string, time: string): string {
  const [h, mi] = time.split(":");
  return `${dateKey}T${pad2(Number(h))}:${pad2(Number(mi ?? 0))}:00`;
}

/**
 * スロットボタン等の表示ラベル（例: `7/16(木) 10:00〜`）を組み立てる。
 * calendarGridUi.formatSlotButtonLabel / bookingSectionUi.formatDesiredDateTimeLabel の共通実装。
 */
export function formatSlotLabel(iso: string): string {
  const p = parseJstDateTime(iso);
  if (!p) return "";
  const wd = weekdayLabel(p.year, p.month, p.day);
  return `${p.month}/${p.day}(${wd}) ${pad2(p.hour)}:${pad2(p.minute)}〜`;
}

/**
 * 希望日程候補の表示ラベル（例: `7月16日（木） 14:00`）を組み立てる。
 * desiredDateTimeInputUi.formatCandidateLabel の共通実装。
 */
export function formatCandidateLabel(iso: string): string {
  const p = parseJstDateTime(iso);
  if (!p) return "";
  const wd = weekdayLabel(p.year, p.month, p.day);
  return `${p.month}月${p.day}日（${wd}） ${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * カレンダーの日付見出しラベル（例: `8月3日(日)の空き時間` の「8月3日(日)」部分）を組み立てる。
 * 日付キー（`YYYY-MM-DD`）から生成する。
 */
export function formatDateHeadingLabel(dateKey: string): string {
  const p = parseJstDateTime(dateKey);
  if (!p) return "";
  const wd = weekdayLabel(p.year, p.month, p.day);
  return `${p.month}月${p.day}日(${wd})`;
}

/** JSTローカル日時文字列から `HH:mm` を切り出す（`split('T')[1]` 相当、`new Date()` 不使用）。 */
export function formatTimeLabel(iso: string): string {
  const p = parseJstDateTime(iso);
  if (!p) return "";
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * キャンセル画面等の日時表示（例: `2099年8月1日 10:00`）を組み立てる。
 * appointmentConfirmationUi.formatDateTime の共通実装。
 */
export function formatCancelDateTime(iso: string | null): string {
  const p = parseJstDateTime(iso);
  if (!p) return "日時未定";
  return `${p.year}年${p.month}月${p.day}日 ${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * Googleカレンダーリンク用の日時フォーマット `YYYYMMDDTHHMMSS`（Zを付与しない）を組み立てる。
 * JSTローカル値をそのまま整形し、呼び出し元が `ctz=Asia/Tokyo` を明示する（datetime-contract.md §6）。
 */
export function formatGoogleCalendarDate(iso: string): string {
  const p = parseJstDateTime(iso);
  if (!p) return "";
  return `${p.year}${pad2(p.month)}${pad2(p.day)}T${pad2(p.hour)}${pad2(p.minute)}${pad2(p.second)}`;
}

/**
 * 現在時刻を JST の壁時計文字列 `yyyy-MM-ddTHH:mm:ss` として返す。
 * 実行環境TZ=Asia/Tokyo 前提（datetime-contract.md §2「現在時刻の生成」）。
 * 端末ローカルの年月日時分秒を組み立てるため、`toISOString()`（UTC化）は使わない。
 */
export function nowJstDateTime(): string {
  const now = new Date();
  return buildRequestDateTime(
    now.getFullYear(),
    now.getMonth() + 1,
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  );
}

/**
 * `scheduledStartAt`（JST壁時計文字列）の指定時間前の期限を JST 壁時計文字列で返す。
 * 分・時の桁上がり/桁下がりを含む算出のため、曜日算出と同様に数値引数の `Date`
 * （壁時計解釈でTZ非依存、frontend-display-spec.md §2.3）を隔離して使用する。
 */
export function subtractHoursJst(iso: string, hours: number): string | null {
  const p = parseJstDateTime(iso);
  if (!p) return null;
  // 数値引数の Date は壁時計解釈（TZ非依存）。ここで時分の桁上げ・繰り下げを安全に行う。
  const d = new Date(p.year, p.month - 1, p.day, p.hour - hours, p.minute, p.second);
  return buildRequestDateTime(
    d.getFullYear(),
    d.getMonth() + 1,
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds()
  );
}
